<template>
  <div v-if="tab" class="w-screen h-screen pb-safe bg-[#1E1B2B] overflow-hidden flex flex-col">
    <top-bar
      v-if="!isMobile"
      :show-back="true"
      @back="showBills ? (showBills = false) : router.back()"
    >
      <template #center>
        <div v-if="tab" class="flex items-center heading-3">
          <span v-if="tab.code">
            <span class="font-bold">{{ tab.code }}</span>
            <span v-if="tab.tableName"> - {{ tab.tableName }}</span>
            <span v-else-if="tab.name"> - {{ tab.name }}</span>
          </span>
          <span v-else-if="tab.tableName">{{ tab.tableName }}</span>
          <span v-else>{{ tab.name }}</span>

          <l-dropdown
            :options="tabOptions"
            :mark-selected="false"
            placement="bottom"
            trigger="click"
          >
            <l-button v-if="tab.open" size="small" type="text" icon="bracket-down" />
          </l-dropdown>
        </div>
      </template>
    </top-bar>
    <top-bar-mobile
      v-else
      show-back
      :show-close="false"
      @back="showBills ? (showBills = false) : router.back()"
    >
      <template #center>
        <div class="flex items-center gap-2 heading-3">
          <span v-if="tab.code">
            <span class="font-bold">{{ tab.code }}</span>
            <span v-if="tab.tableName"> - {{ tab.tableName }}</span>
            <span v-else-if="tab.name"> - {{ tab.name }}</span>
          </span>
          <span v-else-if="tab.tableName">{{ tab.tableName }}</span>
          <span v-else>{{ tab.name }}</span>
        </div>
      </template>
      <template #right>
        <l-button-pos
          v-if="!showBills"
          size="small"
          icon="ticket"
          type="secondary"
          @click="showBills = true"
        >
          {{ t('checkout.bills') }}
          <l-badge :value="bills.length" />
        </l-button-pos>
      </template>
    </top-bar-mobile>

    <div v-show="!showBills" class="flex w-full h-full overflow-hidden gap-4 p-3 justify-center">
      <component :is="isMobile ? Drawer : 'div'" v-model="showItems" class="contents">
        <div
          class="flex flex-col shrink w-full bg-[#2F2D45] sm:rounded-2xl overflow-hidden sm:h-full sm:max-w-[30rem] sm:min-w-[19rem]"
        >
          <div class="flex justify-between items-center sm:p-2 mb-4 sm:mb-0">
            <div class="text-xl font-medium pl-4 sm:pl-2">{{ t('checkout.order-breakdown') }}</div>
            <l-button-pos v-if="!isMobile" icon="ticket" type="secondary" @click="showBills = true">
              {{ t('checkout.bills') }}
              <l-badge :value="bills.length" />
            </l-button-pos>
          </div>

          <checkout-tab
            v-model:payers="payers"
            v-model:mode="paymentMode"
            :selected-bill-id="pendingBill?.id === selectedBill?.id ? undefined : selectedBill?.id"
            :selected-bill="selectedBill"
            :selected-product-ids="selectedProductsIds"
            :tab-id="tabId"
            :dinners="tab.seats.length"
            @amount-changed="setCustomAmount($event)"
            @toggle-product="toggleProduct"
          />

          <div
            class="sticky bottom-0 left-0 right-0 bg-[#3E3C5C] py-3 border-t border-n-800 sm:relative before:absolute before:top-0 before:left-0 before:-mt-4 before:w-full before:h-4 before:bg-[linear-gradient(180deg,rgba(47,45,69,0.00)_0%,#2F2D45_100%)]"
          >
            <terrace-surcharge
              v-if="tableHasSurcharge"
              :tab-id="tabId"
              @toggle="onTerraceSurchargeToggled"
            />
            <balance :tab-id="tabId" />
          </div>
        </div>
      </component>

      <template v-if="!isLg">
        <payment-methods-component :payment-methods="paymentMethods" v-model="paymentMethod" />
      </template>

      <div
        class="flex flex-col gap-4 shrink bg-[#2F2D45] rounded-2xl p-2 pt-4 w-full sm:max-w-[30rem] sm:min-w-[19rem]"
      >
        <div class="flex items-center justify-between">
          <div class="font-medium pl-2 sm:text-xl">{{ t('checkout.current-payment') }}</div>

          <div
            class="flex sm:hidden items-center h-11 px-4 py-2 rounded-lg bg-v-300 shadow-[0_2px_6px_0_rgba(0,0,0,0.35)] cursor-pointer transition-colors"
            @click="showItems = true"
          >
            <template v-if="paymentMode === 'split'">{{ t('checkout.payers-bill') }}</template>
            <template v-else-if="selectedProductsIds.length > 0">
              {{ t('checkout.selected-items-bill', { count: selectedProductsIds.length }) }}
            </template>
            <template v-else>{{ t('checkout.full-bill') }}</template>
          </div>
        </div>

        <template v-if="isLg">
          <payment-methods-component :payment-methods="paymentMethods" v-model="paymentMethod" />
        </template>

        <div
          class="flex items-center justify-between w-full py-1 px-4 h-14 rounded-lg bg-[#1E1B2B] shrink-0"
        >
          <div>
            <template v-if="paymentMode === 'split'">{{ t('checkout.payers-bill') }}</template>
            <template v-else-if="selectedProductsIds.length > 0">
              {{ t('checkout.selected-items-bill', { count: selectedProductsIds.length }) }}
            </template>
            <template v-else>{{ t('checkout.full-bill') }}</template>
          </div>
          <div class="flex items-center">
            {{ currency(toPay) }}
            <l-icon
              name="edit"
              size="medium"
              class="ml-2 cursor-pointer text-v-300"
              @click="showCustomAmount = true"
            />
          </div>
        </div>

        <reservation-deposit-warning :reservation="reservation" />

        <div class="flex flex-col gap-4 overflow-auto">
          <div>{{ hasTip ? t('checkout.total-tip') : t('checkout.received-amount') }}</div>
          <payment-amount-options
            :mode="hasTip ? 'tip' : 'change'"
            :amount="toPay"
            :selected-amount="receivedAmount"
            :charge-amount="toCharge"
            @amount-selected="receivedAmount = $event"
          />

          <div
            class="flex items-center justify-between w-full p-4 rounded-lg border border-[#5D5C8C]"
          >
            <div class="text-n-200">
              {{ hasTip ? t('checkout.tip') : t('checkout.change') }}
            </div>
            <div>
              {{ currency(hasTip ? toTip : toChange) }}
            </div>
          </div>
        </div>

        <div class="mt-auto flex flex-col gap-2">
          <div v-if="isMobile">
            <terrace-surcharge
              v-if="tableHasSurcharge"
              :tab-id="tabId"
              @toggle="onTerraceSurchargeToggled"
            />
            <balance :tab-id="tabId" />
          </div>
          <div v-if="selectedBill" class="flex gap-2">
            <l-button-pos type="vertical" icon="printer" @click="print">
              {{ t('checkout.print-bill') }}
            </l-button-pos>
            <l-button-pos
              type="vertical"
              icon="ticket"
              v-if="!selectedBill.customerCompany"
              @click="showCompanySelector = true"
            >
              {{ t('checkout.create-invoice') }}
            </l-button-pos>
            <l-button-pos
              type="vertical"
              icon="mail-close"
              v-if="selectedBill.customerCompany"
              @click="sendInvoiceBillId = selectedBill.id"
            >
              {{ t('checkout.send-invoice') }}
            </l-button-pos>
            <l-button-pos
              type="vertical"
              icon="percentage"
              :disabled="checkoutCompleteWithPayments"
              @click="showPromotions = true"
            >
              {{ t('checkout.apply-discount') }}
            </l-button-pos>
            <bill-discount
              v-if="showPromotions"
              :discount="discount"
              :original-total="selectedBill && selectedBill.originalTotal"
              :tab-id="tabId"
              @close="showPromotions = false"
              @delete-promotion="deletePromotion"
              @update-promotion="updatePromotion"
            />
          </div>
          <l-button-pos
            v-if="isDelivery"
            class="w-full"
            :disabled="!isAbleToSendBills"
            size="large"
            @click="confirmDelivery()"
          >
            {{ t('checkout.charge-delivery') }}
          </l-button-pos>
          <l-button-pos
            class="w-full"
            type="alternative"
            :disabled="isCharging || !canCharge"
            size="large"
            @click="charge()"
          >
            {{ t('checkout.charge') }}
            {{ currency(toCharge - toChange) }}
          </l-button-pos>
        </div>
      </div>
    </div>

    <div v-show="showBills" class="flex w-full h-full overflow-hidden gap-4 p-3">
      <div class="overflow-y-scroll scrolling-touch w-full flex gap-4">
        <bill
          v-for="(bill, index) in bills"
          class="first:ml-auto last:mr-auto"
          :key="bill.id"
          :bill="bill"
          :pending-bill-id="pendingBill?.id"
          :index="index"
          :total-bills="bills.length"
          @bill-selected="selectBill(bill.id)"
          @print="printBill(bill)"
          @invalidate="invalidateBill(bill.id)"
          @generate-invoice="createInvoice(bill.id)"
          @send-invoice="sendInvoiceBillId = bill.id"
          @cancel-invoice="cancelInvoice(bill.id)"
        />
      </div>
    </div>

    <company-selector
      v-if="showCompanySelector"
      @company-selected="generateInvoice"
      @close="showCompanySelector = false"
    />
    <send-invoice
      v-if="sendInvoiceBillId"
      :bill-id="sendInvoiceBillId"
      @close="sendInvoiceBillId = ''"
    />
    <custom-amount
      v-model:open="showCustomAmount"
      :default-amount="toPay"
      :max-value="toPay"
      :description="t('checkout.custom-charge-description')"
      @new-amount="(setCustomAmount($event), (showCustomAmount = false))"
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { LBadge, LButton, LDropdown, LIcon, useDialog } from '@last/core-ui/paprika'

import Drawer from '@/components/Drawer.vue'
import TopBar from '@/components/TopBar.vue'
import TopBarMobile from '@/components/TopBarMobile.vue'
import { useCheckout } from '@/composables/useCheckout'
import { useCustomerDisplay } from '@/composables/useCustomerDisplay'
import { useMoney } from '@/composables/useMoney'
import { useTabs } from '@/composables/useTabs'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { useFloorplanStore } from '@/store/floorplan'
import { usePromotionsStore } from '@/store/promotions'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { BillWithPayments, Company, Discount } from '@/types'
import { isLg, isMobile } from '@/utils/device-type'

import CompanySelector from '../invoices/CompanySelector.vue'
import SendInvoice from '../invoices/SendInvoice.vue'
import Balance from './Balance.vue'
import Bill from './Bill.vue'
import BillDiscount from './BillDiscount.vue'
import CheckoutTab from './CheckoutTab.vue'
import CustomAmount from './CustomAmount.vue'
import LButtonPos from './LButton.vue'
import PaymentAmountOptions from './PaymentAmountOptions.vue'
import PaymentMethodsComponent from './PaymentMethods.vue'
import ReservationDepositWarning from './ReservationDepositWarning.vue'
import TerraceSurcharge from './TerraceSurcharge.vue'

const route = useRoute()
const router = useRouter()
const dialog = useDialog()
const { currency } = useMoney()
const { t } = useI18n()

const tabId = route.params.tabId as string

const customerDisplay = useCustomerDisplay()
const { addBill, updateTerraceSurcharge, removeBill, addInvoice, regenerateBill } = useTabsStore()
const promotionsStore = usePromotionsStore()
const floorplanStore = useFloorplanStore()
const reservationsStore = useReservationsStore()
const { tables } = storeToRefs(floorplanStore)

const reservation = computed(() => reservationsStore.findReservationByTabId(tabId))

const { tab, allProducts, tabGlobalPromotion, closeTab } = useTabs(() => tabId)
const {
  selectedBillId,
  bills,
  pendingBill,
  selectedBill,
  selectedProductsIds,
  paymentMethods,
  paymentMethod,
  isCharging,
  checkoutCompleteWithPayments,
  toCharge,
  toChange,
  toPay,
  toTip,
  hasTip,
  receivedAmount,
  payers,
  isDelivery,
  charge,
  setCustomAmount,
  generateDeliveryBills
} = useCheckout(tabId)

const showBills = ref(false)
const showPromotions = ref(false)
const showCompanySelector = ref(false)
const showItems = ref(false)
const showCustomAmount = ref(false)
const paymentMode = ref<'items' | 'split'>('items')
// FIXME
const sendInvoiceBillId = ref<string>()

const currentDiscount = ref<{
  type: 'percentage' | '2x1' | 'currency'
  amount: number
  promotionId?: string
  concept?: string
  global?: boolean
  promotionName?: string
} | null>(null)

const tabOptions = computed(() => [
  {
    label: t('ordering.close'),
    value: 'close',
    onClick: () => closeTab()
  }
])

onMounted(() => {
  if (isDelivery.value) {
    const method =
      tab.value.deliveryOrder?.preferredPaymentMethod || tab.value.source?.toLowerCase()
    const preferredPaymentMethod = paymentMethods.value.find(m => m.type === method)
    if (preferredPaymentMethod) {
      paymentMethod.value = preferredPaymentMethod
    }
  }
  if (bills.value && bills.value.length > 0) {
    const firstPendingBill = bills.value.find(
      bill => bill.pending > 0 && pendingBill.value?.id !== bill.id
    )
    selectedBillId.value = firstPendingBill?.id
    if (firstPendingBill?.payers) {
      setCustomAmount(0)
    }
  }
  if (tabGlobalPromotion.value) {
    currentDiscount.value = {
      type: tabGlobalPromotion.value.discountType,
      amount: tabGlobalPromotion.value.discountAmount!,
      promotionId: tabGlobalPromotion.value.promotionId,
      concept: tabGlobalPromotion.value.concept
    }
  }
})

const discount = computed(() => {
  if (selectedBillId.value !== pendingBill.value?.id) {
    return selectedBill.value.discount
  }
  return currentDiscount.value
})

function deletePromotion() {
  promotionsStore.deleteTabPromotion({
    tabId: tabId,
    tabPromotion: tabGlobalPromotion.value!
  })
  currentDiscount.value = null
}

function updatePromotion(discount: Discount) {
  promotionsStore.updateTabPromotion({
    tabId: tabId,
    promotion: {
      discountType: discount.type,
      discountAmount: discount.amount,
      name: discount.promotionName,
      id: discount.promotionId,
      global: discount.global
    }
  })
  currentDiscount.value = discount
}

const canCharge = computed(() => {
  if (!selectedBill.value) return false
  return toPay.value > 0 || (selectedBill.value.total === 0 && !checkoutCompleteWithPayments.value)
})

const tableHasSurcharge = computed(() => {
  if (!tab || !tab.value) return false
  const table = tables.value[tab.value.tables[0]]

  return (
    (table && table.terraceSurchargePercentage > 0) ||
    (table?.flatFeeSurcharge && table?.flatFeeSurcharge > 0) ||
    (tab.value.terraceSurchargePercentage && tab.value.terraceSurchargePercentage > 0) ||
    (tab.value.flatFeeSurcharge && tab.value.flatFeeSurcharge > 0)
  )
})

watch(checkoutCompleteWithPayments, complete => {
  const tabProducts = allProducts.value
  if (complete && tabProducts.length > 0) {
    closeTab()
  }
})

watch(
  selectedBill,
  newBill => {
    if (newBill) {
      customerDisplay.updateBill(newBill)
    } else {
      customerDisplay.clearDisplay()
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  customerDisplay.clearDisplay()
})

function print() {
  selectedBillId.value = selectedBill.value.id
  addBill({ tabId: tabId, bill: selectedBill.value })
  TicketPrinter.printBill(selectedBill.value)
  selectedProductsIds.value = []
}

function printBill(bill: BillWithPayments) {
  TicketPrinter.printBill(bill)
}

function selectBill(billId: string) {
  selectedProductsIds.value = []
  selectedBillId.value = billId
  showBills.value = false
}

function createInvoice(billId: string) {
  selectedBillId.value = billId
  showCompanySelector.value = true
}

function generateInvoice(customerCompany: Company) {
  addInvoice({ tabId: tabId, bill: selectedBill.value, customerCompany })
  showCompanySelector.value = false
  selectedProductsIds.value = []
}

function toggleProduct(productId: { id: string; uniqueId: string }): void {
  if (selectedProductsIds.value.map(id => id.uniqueId).includes(productId.uniqueId)) {
    selectedProductsIds.value = selectedProductsIds.value.filter(
      id => id.uniqueId != productId.uniqueId
    )
  } else {
    selectedProductsIds.value = [...selectedProductsIds.value, productId]
  }
  selectedBillId.value = pendingBill.value!.id
}

function onTerraceSurchargeToggled(enabled: boolean): void {
  updateTerraceSurcharge({
    tabId,
    enabled
  })
}

async function cancelInvoice(billId: string) {
  selectedBillId.value = regenerateBill({
    tabId: tabId,
    billId: billId,
    customerCompany: undefined
  })
}

function invalidateBill(billId: string) {
  dialog({
    title: t('checkout.invalidate-bill'),
    content: t('checkout.invalidate-bill-content'),
    mainLabel: t('ctas.yes'),
    secondaryLabel: t('ctas.no'),
    onConfirm: () => {
      removeBill({ tabId: tabId, billId })
      if (pendingBill.value) {
        selectedBillId.value = pendingBill.value.id
      } else if (bills.value && bills.value.length > 0) {
        selectedBillId.value = bills.value[0].id
      }
    }
  })
}

const isAbleToSendBills = computed(() => {
  const existingPaymentMethod = paymentMethods.value.find(
    method => paymentMethod.value.type === method.type
  )
  return (
    tab.value &&
    ((tab.value.pickupType === 'ownDelivery' && !!existingPaymentMethod) ||
      tab.value.pickupType === 'delivery')
  )
})

function confirmDelivery() {
  if (!isAbleToSendBills.value) return
  generateDeliveryBills()
  router.push({ name: 'delivery', query: { preSelectedTabId: tabId } })
}
</script>
